import { z } from 'zod';
import { clientRequestIdSchema, instantSchema, localDateSchema, objectIdSchema } from './common.js';
/**
 * Weights are stored in kilograms. The bounds are deliberately wide: they exist
 * to catch a mistyped "755" rather than to police what a plausible weight is.
 */
export const weightKgSchema = z.number().min(20).max(500);
/**
 * Body fat as a percentage. The bounds span what a consumer scale can plausibly
 * report — roughly essential-fat minimum to severe obesity — and exist to catch
 * a misplaced decimal rather than to judge a body.
 */
export const bodyFatPercentSchema = z.number().min(3).max(70);
export const createWeightSchema = z.object({
    weightKg: weightKgSchema,
    /** Optional: only scales that measure it report a value. */
    bodyFatPercent: bodyFatPercentSchema.nullable().optional(),
    /** Defaults to "now" — the common case is logging the reading immediately. */
    measuredAt: instantSchema.optional(),
    note: z.string().trim().max(280).optional(),
    /** Present when the write came from the offline queue. */
    clientRequestId: clientRequestIdSchema.optional(),
});
export const updateWeightSchema = z
    .object({
    weightKg: weightKgSchema.optional(),
    bodyFatPercent: bodyFatPercentSchema.nullable().optional(),
    measuredAt: instantSchema.optional(),
    note: z.string().trim().max(280).nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'no fields to update');
export const weightQuerySchema = z.object({
    from: instantSchema.optional(),
    to: instantSchema.optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export const weightEntrySchema = z.object({
    id: objectIdSchema,
    weightKg: z.number(),
    /** Null when the reading carried no body-fat measurement. */
    bodyFatPercent: z.number().nullable(),
    measuredAt: z.string(),
    note: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * One calendar day of weigh-ins, collapsed. Multiple readings a day are the
 * norm, so the series the chart draws uses the daily average.
 */
export const weightDaySchema = z.object({
    date: localDateSchema,
    average: z.number(),
    min: z.number(),
    max: z.number(),
    count: z.number().int(),
    /** Exponentially smoothed trend value for this day. */
    trend: z.number().nullable(),
    /** Mean body fat for the day; null when no reading carried one. */
    bodyFatAverage: z.number().nullable(),
    /**
     * Smoothed body fat, on the same EMA as weight. Bioimpedance readings swing
     * by whole percentage points with hydration, so the raw series is even less
     * readable than raw weight.
     */
    bodyFatTrend: z.number().nullable(),
});
/**
 * A projection of when the weight goal is reached, derived from the trend's
 * slope over the returned span.
 */
export const weightProjectionSchema = z.object({
    targetKg: z.number(),
    /**
     * - `reached` — the trend is at or past the target
     * - `on_track` — heading there; `etaDate` is set
     * - `diverging` — flat, or moving away from the target
     * - `insufficient_data` — too few weigh-ins, or too short a span, to say
     *   anything honest. Two days of readings cannot support a date.
     */
    status: z.enum(['reached', 'on_track', 'diverging', 'insufficient_data']),
    /** Estimated date the trend crosses the target; only set when `on_track`. */
    etaDate: localDateSchema.nullable(),
});
export const weightSeriesSchema = z.object({
    days: z.array(weightDaySchema),
    latest: weightEntrySchema.nullable(),
    /** Change in the trend line over the requested window, in kg. */
    changeKg: z.number().nullable(),
    /**
     * Slope of the trend over the returned span, in kg per week.
     *
     * Null until there are enough weigh-ins over a long enough span to mean
     * anything — extrapolating a single day's change to a week produces figures
     * like "−13 kg/week", which is worse than showing nothing.
     */
    ratePerWeekKg: z.number().nullable(),
    /** Goal projection; null when no weight goal is set or no trend exists. */
    projection: weightProjectionSchema.nullable(),
});
export const weightSeriesQuerySchema = z.object({
    from: instantSchema.optional(),
    to: instantSchema.optional(),
    /** Window size of the exponential moving average, in days. */
    trendWindow: z.coerce.number().int().min(1).max(31).default(7),
});
//# sourceMappingURL=weight.js.map